import { ErrorProvider, LabelProvider, Main, MainContent } from '@teliaee/design-system';
import { RouterStore } from '@superwf/mobx-react-router';
import { BrowserHistory, MemoryHistory, createBrowserHistory, createMemoryHistory } from 'history';
import { Provider, observer } from 'mobx-react';
import * as React from 'react';
import { BrowserRouter, MemoryRouter, Route, Routes } from 'react-router-dom';
import { SSOClient } from './CommonInterfaces';
import ContentLoadWrapper from './components/ContentLoadWrapper';
import TitleHandler from './components/TitleHandler';
import Config from './Config';
import ComponentErrorBoundary from './error/ComponentErrorBoundary';
import SsoHeader from './header/SsoHeader';
import { ROUTES } from './Routes';
import GaDataStore from './stores/GaDataStore';
import RootStore from './stores/RootStore';
import { API, reInit } from './util/ApiUtil';
import SsoThemeProvider from './util/SsoThemeProvider';
import ErrorUtil from './util/ErrorUtil';
import { addSsoGtmScript, cleanUpSsoPortalRoot, injectSsoPortalRoot } from './util/HtmlUtil';
import PaddedSpinner from './util/PaddedSpinner';
import AnalyticsWrapper from './wrapper/AnalyticsWrapper';

interface AppProps {
  config?: InitialConfig;
  client?: SSOClient;
  initialUrl?: string;
}

@observer
export default class App extends React.Component<AppProps> {
  private history?: BrowserHistory | MemoryHistory;
  private rootStore: RootStore;
  private routerStore: RouterStore;
  private readonly isModule: boolean;

  constructor(props: AppProps) {
    super(props);
    this.isModule = !!props?.config;
    this.history = this.setupHistory();
    this.routerStore = new RouterStore(this.history);
    this.rootStore = new RootStore(this.routerStore, props.client);
    if (this.isModule) {
      this.setModuleConfig(props.config!.apiUrl);
      addSsoGtmScript();
    }
  }

  componentDidMount() {
    if (this.isModule) {
      GaDataStore.openModuleView();
      injectSsoPortalRoot();
    }
  }

  componentWillUnmount() {
    if (this.isModule) {
      cleanUpSsoPortalRoot();
    }
  }

  private setupHistory = () => {
    return this.isModule
      ? createMemoryHistory({
          initialIndex: 0,
          initialEntries: this.props?.initialUrl ? [this.props.initialUrl] : [],
        })
      : createBrowserHistory();
  };

  private setModuleConfig = async (apiUrl: string) => {
    Config.setIsModule(true);
    Config.setFrontendUrl(apiUrl?.replace('/api', ''));
    Config.setBrand(this.props.config?.brand);
    Config.setTheme(this.props.config?.theme);
    Config.setApiUrl(apiUrl);
    reInit(apiUrl);
    if (!Config.initialized) {
      const configResponse = await API.get(`/settings/config${window.location.search}`);
      Config.initialize(configResponse.data);
    }
  };

  private logFrontendErrors(e: string) {
    const error: Error = new Error('Errors in frontend components');
    error.stack = e;
    ErrorUtil.pushErrorToTeliaRum(error);
  }

  private getRoutes() {
    return ROUTES.map((route, index) => {
      return <Route caseSensitive={false} path={route.path} element={route.component} key={`route-${index}`} />;
    });
  }

  private renderRoutes() {
    return (
      <React.Suspense fallback={<PaddedSpinner />}>
        {this.rootStore.translateStore.fetched ? <Routes location={this.routerStore.location}>{this.getRoutes()}</Routes> : <PaddedSpinner />}
      </React.Suspense>
    );
  }

  private renderApp() {
    return (
      <ComponentErrorBoundary history={this.history}>
        <AnalyticsWrapper>
          <ContentLoadWrapper>
            {!this.isModule ? (
              <>
                <TitleHandler />
                <SsoHeader />
                <MainContent>{this.renderRoutes()}</MainContent>
              </>
            ) : (
              this.renderRoutes()
            )}
          </ContentLoadWrapper>
        </AnalyticsWrapper>
      </ComponentErrorBoundary>
    );
  }

  private get isIframe() {
    return window.top !== window;
  }

  public render() {
    const RouterVariant = this.isModule ? MemoryRouter : BrowserRouter;
    return (
      <div className={this.isModule ? 'sso-root' : undefined} id={this.isModule ? 'sso-root' : undefined}>
        <Provider {...this.rootStore}>
          <RouterVariant>
            <ErrorProvider onError={this.logFrontendErrors}>
              <SsoThemeProvider isModule={this.isModule} brand={this.props.config?.brand} theme={this.props.config?.theme}>
                <LabelProvider labels={this.rootStore.translateStore.globalTranslations}>
                  {!this.isModule ? <Main background={this.isIframe ? 'neutral-bg-level-3' : undefined}>{this.renderApp()}</Main> : this.renderApp()}
                </LabelProvider>
              </SsoThemeProvider>
            </ErrorProvider>
          </RouterVariant>
        </Provider>
      </div>
    );
  }
}
